import {Alert, Card, closeCurrentDialog, DataStatus, openDialog} from 'platform/components';
import {Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  AftersalesPaymentMethod,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentsQuery,
} from '@dms/api/sales';
import {useGetDirectSaleQuery} from '@dms/api/warehouse';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrderJobsQuery,
  useGetServiceCaseServiceOrderMissingMandatoryFieldsApiQuery,
  useGetServiceOrderQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes, workshopRoutes} from '@dms/routes';
import {
  MandatoryFieldMapValue,
  MandatoryFieldsErrorModal,
  queryParams,
  useTenant,
} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {ForeginCurrencyPaymentCard} from '../AftersalesCheckoutPayment/components/ForeginCurrencyPaymentCard';
import {CheckoutPaymentCardV2} from './components/CheckoutPaymentCardV2';
import {CorrectiveTaxDocumentsListV2} from './components/CorrectiveTaxDocumentsListV2';
import {DepositPaymentsCardV2} from './components/DepositPaymentsCardV2';
import {ContextTarget, RestrictionActionContext} from './constants/issueRestrictionAlerts';

interface AftersalesCheckoutPaymentV2Props extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

type PathType = '/vehicles/:id/edit' | '/vehicles/:id/edit/:tab';

const isPathType = (href: string): href is PathType =>
  href === vehiclesRoutes.editV2 || href === vehiclesRoutes.editV2Tab;

const VEHICLE_MANDATORY_FIELDS_MAP: Record<string, MandatoryFieldMapValue> = {
  vehicleRegistrationPlate: {
    title: i18n.t('entity.vehicle.labels.licensePlate'),
    href: vehiclesRoutes.editV2,
    formField: 'registrationPlate',
  },
  vehicleVin: {
    title: i18n.t('entity.vehicle.labels.vin'),
    href: vehiclesRoutes.editV2,
    formField: 'vin',
  },
  vehicleFirstRegistrationOn: {
    title: i18n.t('entity.vehicle.labels.firstRegistration'),
    href: vehiclesRoutes.editV2,
    formField: 'firstRegistrationOn',
  },
  vehicleWarrantyDate: {
    title: i18n.t('entity.vehicle.labels.warrantyUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'warrantyDate',
  },
  vehicleTechnicalInspectionValidUntil: {
    title: i18n.t('entity.vehicle.labels.inspectionValidUntil'),
    href: vehiclesRoutes.editV2Tab,
    tab: 'vehicle-data',
    formField: 'technicalInspectionValidUntil',
  },
};

const SERVICE_ORDER_MANDATORY_FIELDS_MAP: Record<string, MandatoryFieldMapValue> = {
  completionAtDate: {
    title: i18n.t('entity.order.labels.mandatoryField-completionAtDate'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'completionAtDate',
  },
  totalPriceEstimated: {
    title: i18n.t('entity.order.labels.mandatoryField-totalPriceEstimated'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'totalPriceEstimated',
  },
  provider: {
    title: i18n.t('entity.order.labels.mandatoryField-provider'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'provider',
  },
  approvalNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-approvalNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'approvalNumber',
  },
  serviceCenterId: {
    title: i18n.t('entity.order.labels.mandatoryField-serviceCenterId'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'serviceCenterId',
  },
  serviceOrderGroupId: {
    title: i18n.t('entity.order.labels.mandatoryField-serviceOrderGroupId'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'serviceOrderGroupId',
  },
  mileage: {
    title: i18n.t('entity.order.labels.mandatoryField-mileage'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'mileage',
  },
  fuelTank: {
    title: i18n.t('entity.order.labels.mandatoryField-fuelTank'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'fuelTank',
  },
  generatingOrderSheet: {
    title: i18n.t('entity.order.labels.mandatoryField-generatingOrderSheet'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'generatingOrderSheet',
  },
  insuranceCompany: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceCompany'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceCompany',
  },
  insuranceType: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceType'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceType',
  },
  insuranceEventNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceEventNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceEventNumber',
  },
  insuranceClaimNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceClaimNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceClaimNumber',
  },
  insuranceClaimOccurrenceAt: {
    title: i18n.t('entity.order.labels.mandatoryField-insuranceClaimOccurrenceAt'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'insuranceClaimOccurrenceAt',
  },
  applicationOfTheLaw: {
    title: i18n.t('entity.order.labels.applicationOfTheLaw'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'applicationOfTheLaw',
  },
  claimSerialNumber: {
    title: i18n.t('entity.order.labels.mandatoryField-claimSerialNumber'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'claimSerialNumber',
  },
  costCenter: {
    title: i18n.t('entity.order.labels.mandatoryField-costCenter'),
    href: workshopRoutes.serviceCaseDetail,
    tab: 'detail',
    formField: 'costCenter',
  },
};

export function AftersalesCheckoutPaymentV2(props: AftersalesCheckoutPaymentV2Props) {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});
  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useGetCheckoutPaymentsQuery({checkoutId: props.checkoutId});

  const {data: missingMandatoryFieldsData} =
    useGetServiceCaseServiceOrderMissingMandatoryFieldsApiQuery(
      {
        serviceCaseId: props.serviceCaseId ?? '',
        serviceOrderId: props.serviceOrderId ?? '',
        event: 'BEFORE_CLOSING',
      },
      {
        skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId),
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
      }
    );

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId ?? ''},
    {skip: isNilOrEmpty(props.serviceCaseId)}
  );

  const {data: serviceOrderJobs} = useGetServiceCaseOrderJobsQuery(
    {
      serviceCaseId: props.serviceCaseId ?? '',
      serviceOrderId: props.serviceOrderId ?? '',
    },
    {
      skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId),
    }
  );

  const {data: serviceOrder} = useGetServiceOrderQuery(
    {
      serviceCaseId: props.serviceCaseId ?? '',
      serviceOrderId: props.serviceOrderId ?? '',
    },
    {skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)}
  );

  const isLoading = isCheckoutLoading || isPaymentsLoading;
  const isError = isCheckoutError || isPaymentsError;

  const contextTarget: ContextTarget = match(checkoutData?.context?.target)
    .with('service-order', () => 'service-order' as const)
    .with('direct-sale', () => 'direct-sale' as const)
    .otherwise(() => undefined);

  const directSaleId = contextTarget === 'direct-sale' ? checkoutData?.context?.id : undefined;

  const {data: directSale} = useGetDirectSaleQuery(
    {directSaleId: directSaleId ?? ''},
    {skip: isNilOrEmpty(directSaleId)}
  );

  const hasMissingMandatoryFields =
    isNotNil(missingMandatoryFieldsData) && (missingMandatoryFieldsData?.length ?? 0) > 0;

  const areAllPaymentsConcept = !!paymentsData?.every(
    ({paymentState}) => paymentState === 'CONCEPT'
  );

  const balancePayment = paymentsData?.find((payment) => payment.discriminator === 'BALANCE');

  const hasDepositPayment = paymentsData?.some((payment) => payment.discriminator === 'DEPOSIT');

  const hasNotPaidDepositPayment = paymentsData?.some(
    (payment) => payment.discriminator === 'DEPOSIT' && payment.paymentState !== 'PAID'
  );

  const correctivePayments = paymentsData?.filter(
    (payment) =>
      payment.discriminator === 'BALANCE_CORRECTION_FULL' ||
      payment.discriminator === 'BALANCE_CORRECTION_PARTIAL'
  );

  const rentalOrderId = serviceOrderJobs?.find(
    (job) => job?.isCarRental && isNotNilOrEmpty(job?.carRentalDetails?.rentalOrderId)
  )?.carRentalDetails?.rentalOrderId;

  const actionContext: RestrictionActionContext = {
    rentalOrderId: rentalOrderId ?? undefined,
    serviceOrderVariantId: serviceOrder?.serviceOrderVariantId,
    directSaleVariantId: directSale?.directSaleVariantId,
  };

  const handleRedirect = (field: MandatoryFieldMapValue) => {
    closeCurrentDialog();

    const isServiceOrderField = field.href === workshopRoutes.serviceCaseDetail;

    if (isServiceOrderField) {
      const path = composePath(workshopRoutes.serviceCaseDetail, {
        params: {id: props.serviceCaseId ?? ''},
        queryParams: {
          [queryParams.SERVICE_CASE_ORDER_ID]: props.serviceOrderId ?? '',
          [queryParams.SERVICE_CASE_ORDER_TAB]: field.tab ?? 'detail',
        },
      });

      setTimeout(() => {
        navigate(path);
      }, 100);
      return;
    }

    if (!isPathType(field.href) || isNilOrEmpty(serviceCase?.vehicleId)) {
      return;
    }

    const path = composePath(field.href, {
      params: {id: serviceCase?.vehicleId, tab: isNotNilOrEmpty(field.tab) ? field.tab : undefined},
    });

    navigate(path);
  };

  const showMandatoryFieldsErrorModal = () => {
    const vehicleFields =
      missingMandatoryFieldsData?.filter((field) => field?.resourceId === 'VEHICLE') ?? [];
    const serviceOrderFields =
      missingMandatoryFieldsData?.filter((field) => field?.resourceId === 'SERVICE_ORDER') ?? [];

    const transformedVehicleFields: MandatoryFieldMapValue[] = vehicleFields
      .map((field) => VEHICLE_MANDATORY_FIELDS_MAP[field?.mandatoryField ?? ''])
      .filter(Boolean);

    const transformedServiceOrderFields: MandatoryFieldMapValue[] = serviceOrderFields
      .map((field) => SERVICE_ORDER_MANDATORY_FIELDS_MAP[field?.mandatoryField ?? ''])
      .filter(Boolean);

    const transformedMissingFields = [
      ...transformedVehicleFields,
      ...transformedServiceOrderFields,
    ];

    const translatedFields = transformedMissingFields.map((field) => field.title);

    openDialog(
      <MandatoryFieldsErrorModal
        mandatoryFields={transformedMissingFields}
        translatedFields={translatedFields}
        onSubmit={handleRedirect}
      />,
      {
        id: 'mandatory-fields-error-modal',
        title: i18n.t('entity.mandatoryFields.errorModal.title'),
      }
    );
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Show when={hasMissingMandatoryFields}>
          <Alert
            variant="error"
            title={`${i18n.t('entity.mandatoryFields.labels.missingFieldsCount')} (${missingMandatoryFieldsData?.length ?? 0})`}
            hyperlinks={[
              {
                title: i18n.t('general.actions.view'),
                onClick: () => {
                  showMandatoryFieldsErrorModal();
                },
              },
            ]}
          />
        </Show>
        {checkoutData && (
          <Card variant="inlineGrey">
            <VStack spacing={1}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.order.labels.totalAmount')}
              </Text>
              <Heading size={3}>
                {formatCurrency(
                  checkoutData?.totalPrice?.withVat?.amount ?? 0,
                  checkoutData?.totalPrice?.withVat?.currency ?? tenant.tenantCurrency,
                  2
                )}
              </Heading>
              <Text size="xSmall" color="secondary">
                {`${formatCurrency(
                  checkoutData?.totalPrice?.withoutVat?.amount ?? 0,
                  checkoutData?.totalPrice?.withoutVat?.currency ?? tenant.tenantCurrency,
                  2
                )} ${i18n.t('entity.order.labels.withoutVat')}`}
              </Text>
            </VStack>
          </Card>
        )}
        <Show when={checkoutData?.isEditForeignCurrencyAllowed}>
          <ForeginCurrencyPaymentCard
            checkoutId={props.checkoutId}
            areAllPaymentsConcept={areAllPaymentsConcept}
            isEditingDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('foreginCurrency', props)}
          />
        </Show>
        <Show when={checkoutData?.isEditDepositAllowed}>
          <DepositPaymentsCardV2
            checkoutId={props.checkoutId}
            defaultPaymentType={props.defaultPaymentType}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            data-testid={suffixTestId('depositPayments', props)}
          />
        </Show>
        <HStack spacing={2}>
          <Icon value="action/payment" />
          <Heading size={4}>
            {hasDepositPayment
              ? i18n.t('entity.checkout.labels.supplementaryPayment')
              : i18n.t('entity.checkout.labels.totalPayment')}
          </Heading>
        </HStack>
        {balancePayment && (
          <>
            <Card variant="inlineGrey">
              <VStack spacing={4}>
                <Show when={hasNotPaidDepositPayment}>
                  <Alert
                    title={i18n.t(
                      'general.notifications.paymentNotPossibleBeforeDepositsPaid.title'
                    )}
                    variant="warning"
                  />
                </Show>
                <CheckoutPaymentCardV2
                  paymentId={balancePayment.checkoutPaymentId}
                  checkoutId={props.checkoutId}
                  customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
                  defaultPaymentType={props.defaultPaymentType}
                  isEditingDisabled={props.isEditingDisabled}
                  isCompletingDisabled={props.isCompletingDisabled}
                  serviceCaseId={props.serviceCaseId}
                  serviceOrderId={props.serviceOrderId}
                  contextTarget={contextTarget}
                  contextId={props.serviceCaseId || checkoutData?.context?.id}
                  actionContext={actionContext}
                  data-testid={suffixTestId('payment', props)}
                />
              </VStack>
            </Card>
            <CorrectiveTaxDocumentsListV2
              checkoutId={props.checkoutId}
              customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
              correctivePayments={correctivePayments}
              serviceCaseId={props.serviceCaseId}
              serviceOrderId={props.serviceOrderId}
              isEditingDisabled={props.isEditingDisabled}
              isCompletingDisabled={props.isCompletingDisabled}
              data-testid={suffixTestId('correctiveTaxDocuments', props)}
            />
          </>
        )}
      </VStack>
    </DataStatus>
  );
}
